import { useId, type ReactNode } from "react";
import { checkoutCopy } from "./checkout-copy";
import "./checkout.css";

export interface CheckoutLayoutProps {
  children: ReactNode;
  summary: ReactNode;
  sessionPending?: boolean;
  userEmail?: string;
  backHref?: string;
  loginHref?: string;
  onLogin?: () => void;
  /** Campaign bar pinned above the header. */
  banner?: ReactNode;
}

export function CheckoutSessionLoading() {
  return (
    <div className="checkout-session-loading" role="status" aria-live="polite">
      <img
        src="/checkout-brand/key-lavender.svg"
        alt=""
        aria-hidden="true"
        width="56"
        height="56"
        draggable={false}
      />
      <div>
        <strong>{checkoutCopy.loadingTitle}</strong>
        <p>{checkoutCopy.loadingDescription}</p>
      </div>
    </div>
  );
}

export interface CheckoutLaunchBarProps {
  code: string;
  label: string;
  text: string;
  action?: ReactNode;
}

export function CheckoutLaunchBar({
  code,
  label,
  text,
  action,
}: CheckoutLaunchBarProps) {
  return (
    <aside className="checkout-launch-bar" aria-label={label}>
      <p>
        {text} <strong>{code}</strong>
      </p>
      {action}
    </aside>
  );
}

export function CheckoutLayout({
  children,
  summary,
  sessionPending = false,
  userEmail,
  backHref = "https://hey404.com/#cursos",
  loginHref = "/login?next=%2Fcheckout",
  onLogin,
  banner,
}: CheckoutLayoutProps) {
  const id = useId();
  const currentStep = !sessionPending && userEmail ? 1 : 0;

  return (
    <div className="checkout-shell">
      {banner}
      <header className="checkout-header">
        <div className="checkout-header-inner">
          <a
            className="checkout-logo"
            href={backHref}
            aria-label={checkoutCopy.brand}
          >
            <img src="/logo-final.svg" alt="" width="120" height="80" />
          </a>
          <nav
            className="checkout-header-links"
            aria-label={checkoutCopy.brand}
          >
            <a className="checkout-back" href={backHref}>
              <span aria-hidden="true">←</span> {checkoutCopy.back}
            </a>
            {!sessionPending &&
              !userEmail &&
              (onLogin ? (
                <button
                  type="button"
                  className="checkout-login"
                  onClick={onLogin}
                >
                  {checkoutCopy.login}
                </button>
              ) : (
                <a className="checkout-login" href={loginHref}>
                  {checkoutCopy.login}
                </a>
              ))}
          </nav>
        </div>
      </header>

      <main className="checkout-main">
        <div className="checkout-intro">
          <div>
            <h1 className="checkout-title">
              <span className="checkout-title-lead">{checkoutCopy.title}</span>{" "}
              <span className="checkout-title-ending">
                <span className="checkout-title-sticker">
                  {checkoutCopy.titleHighlight}
                </span>
              </span>
            </h1>
            <p className="checkout-introduction">{checkoutCopy.introduction}</p>
          </div>
          <ol
            className="checkout-steps"
            aria-label={checkoutCopy.progressLabel}
          >
            {checkoutCopy.steps.map((label, index) => (
              <li
                key={label}
                aria-current={index === currentStep ? "step" : undefined}
              >
                <span className="checkout-step-number" aria-hidden="true">
                  {index < currentStep ? "✓" : `0${index + 1}`}
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Price first: the order summary sits on the left, the email and
            the pay button on the right. */}
        <div className="checkout-columns">
          <aside className="checkout-summary" aria-labelledby={`${id}-summary`}>
            <div className="checkout-summary-heading">
              <p>{checkoutCopy.summaryLabel}</p>
              <h2 id={`${id}-summary`}>{checkoutCopy.summaryTitle}</h2>
              <img
                src="/checkout-brand/flower-pink.svg"
                alt=""
                aria-hidden="true"
                width="66"
                height="66"
                draggable={false}
              />
            </div>
            <div className="checkout-summary-body">{summary}</div>
          </aside>

          <section className="checkout-access" aria-labelledby={`${id}-access`}>
            <div className="checkout-access-heading">
              <img
                src="/checkout-brand/face-lime.svg"
                alt=""
                aria-hidden="true"
                width="42"
                height="42"
                draggable={false}
              />
              <div>
                <h2 id={`${id}-access`}>
                  {userEmail
                    ? checkoutCopy.accountTitle
                    : checkoutCopy.accessTitle}
                </h2>
                {userEmail && <p>{checkoutCopy.accountDescription}</p>}
              </div>
            </div>
            {userEmail && <p className="checkout-account-email">{userEmail}</p>}
            <div className="checkout-access-body">
              {sessionPending ? <CheckoutSessionLoading /> : children}
            </div>
          </section>
        </div>

        <p className="checkout-help">
          {checkoutCopy.help}{" "}
          <a href="mailto:hey404lab@gmail.com">{checkoutCopy.helpLink}</a>
        </p>
      </main>
    </div>
  );
}
